import React from 'react';
import SectionHeader from '@components/common/SectionHeader';
import DataTable, { DataTableColumn } from '@components/common/DataTable';
import { formatWithUnit, PLACEHOLDER } from '@utils/format';
import type { InvestmentIndicator, ScheduleItem } from '@app-types/performance';

export interface InvestmentIndicatorsSectionProps {
  indicators: InvestmentIndicator[];
  schedule: ScheduleItem[];
}

interface IndicatorRowView {
  label: string;
  company: string;
  industry: string;
}

interface ScheduleRowView {
  label: string;
  date: string;
}

const indicatorColumns: Array<DataTableColumn<IndicatorRowView>> = [
  { key: 'label', header: '', isRowHeader: true, align: 'left' },
  { key: 'company', header: '企業値', align: 'right', nowrap: true },
  { key: 'industry', header: '業種平均', align: 'right', nowrap: true },
];

const scheduleColumns: Array<DataTableColumn<ScheduleRowView>> = [
  { key: 'label', header: '', isRowHeader: true, align: 'left' },
  { key: 'date', header: '', align: 'right', nowrap: true },
];

/**
 * Section 3: Investment indicators + schedule.
 * Desktop: two independent columns with their own headers (Figma 3124:2419).
 */
const InvestmentIndicatorsSection: React.FC<InvestmentIndicatorsSectionProps> = ({
  indicators,
  schedule,
}) => {
  const indicatorRows: IndicatorRowView[] = indicators.map((ind) => ({
    label: ind.label,
    company: formatWithUnit(ind.companyValue, ind.unit, ind.fractionDigits),
    industry: formatWithUnit(ind.industryValue, ind.unit, ind.fractionDigits),
  }));

  const scheduleRows: ScheduleRowView[] = schedule.map((item) => ({
    label: item.label,
    date: item.date ?? PLACEHOLDER,
  }));

  return (
    <section className="investment-indicators" aria-label="投資指標とスケジュール">
      <div className="investment-indicators__col">
        <SectionHeader title="投資指標" showHelp />
        <DataTable<IndicatorRowView>
          columns={indicatorColumns}
          rows={indicatorRows}
          caption="投資指標"
        />
      </div>
      <div className="investment-indicators__col">
        <SectionHeader title="スケジュール" showHelp />
        <DataTable<ScheduleRowView>
          columns={scheduleColumns}
          rows={scheduleRows}
          caption="スケジュール"
          showHeader={false}
        />
      </div>
    </section>
  );
};

export default InvestmentIndicatorsSection;
